import type { RequestOptions } from 'searchkit';

import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import {
  APP_OPTIONS,
  INFOLEP_HOSTS,
  INFOLEP_DOMAIN,
  INFONTD_DOMAIN,
} from '../../config/constants';

export const searchAttributes = [
  {
    field: 'title',
    weight: 5, // ElasticSearch boost
  },
  {
    field: 'keyword',
    weight: 2,
  },
  {
    field: 'bibcite_abstract',
    weight: 3,
  },
  {
    field: 'bibcite_secondary_title_searchable',
    weight: 2,
  },
  {
    field: 'bibcite_publisher_searchable',
    weight: 1,
  },
  {
    field: 'name',
    weight: 2,
  },
  {
    field: 'taxonomy_vocabulary_3_fulltext',
    weight: 2,
  },
  {
    field: 'ntd_searchable',
    weight: 2,
  },
];

export const highlightAttributes = [
  'title',
];

export const resultAttributes = [
  // Basic fields.
  'url',
  'type',
  'title',
  'summary',
  'nid',
  'field_domain_access',

  // Bibcite fields.
  'bibcite_url',
  'name',

  // Bibcite metadata fields.
  'bibcite_secondary_title',
  'bibcite_publisher',
  'bibcite_year',
  'bibcite_volume',
  'bibcite_issue',
  'bibcite_pages',

  // Other fields
  'date',
  'end_date',
  'location_name',
  'languages', // Online Courses - not a content-type language
];

export const facetAttributes: Exclude<SearchkitWrapperProps['searchSettings']['facet_attributes'], undefined> = [
  {
    attribute: 'type',
    field: 'type',
    type: 'string',
  },
  {
    attribute: 'publication_type',
    field: 'publication_type',
    type: 'string',
  },
  {
    attribute: 'ntd',
    field: 'ntd',
    type: 'string',
  },
  {
    attribute: 'author',
    // @TODO: Might want to change this.
    // This now seems to just return the last name.
    field: 'name.keyword',
    type: 'string',
  },
  {
    attribute: 'subject',
    field: 'taxonomy_vocabulary_3_fulltext.keyword',
    type: 'string',
  },
  {
    attribute: 'journal',
    field: 'bibcite_secondary_title',
    type: 'string',
  },
  {
    attribute: 'target_audience',
    field: 'target_audience_name',
    type: 'string',
  },
  {
    attribute: 'publication_year',
    field: 'bibcite_year',
    type: 'numeric',
  },
  {
    attribute: 'language',
    field: 'bibcite_lang',
    type: 'string',
  },
  {
    attribute: 'regional_focus',
    field: 'regional_focus',
    type: 'string',
  },
  {
    attribute: 'country_code',
    field: 'country_code',
    type: 'string',
  },
  {
    attribute: 'organization_type',
    field: 'organization_type',
    type: 'string',
  },
  {
    attribute: 'research_group',
    field: 'research_group',
    type: 'string',
  },
];

export const sorting: Exclude<SearchkitWrapperProps['searchSettings']['sorting'], undefined> = {
  default: {
    field: '_score',
    order: 'desc',
  },
  _bibcite_year: {
    field: 'bibcite_year',
    order: 'desc',
  },
};

const currentDomain = INFOLEP_HOSTS.includes(window.location.host)
  ? INFOLEP_DOMAIN
  : INFONTD_DOMAIN;

const year = new Date().getFullYear();
const decay = APP_OPTIONS.parameters.searchDecay;

/**
 * Sanitizer for invisible / invalid characters.
 *
 * This is for safety of using older devices and browsers where syntax errors can occur.
 *
 * @param query - The query from the user input.
 * @returns The sanitized query.
 */
function sanitizeQuery(query: string): string {
  return query
    // Remove ASCII control characters
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F]/g, '')
    // Remove zero-width and bidi control characters
    .replace(/[\u200B-\u200F\u202A-\u202E\u2060-\u206F]/g, '')
    // Replace non-breaking spaces with normal ones
    .replace(/\u00A0/g, ' ')
    .trim();
}

export const requestOptions: RequestOptions = {
  getQuery: (query, search_attributes) => {
    const sanitizedQuery = sanitizeQuery(query);

    const getFieldsMap = (boostMultiplier: number) => search_attributes.map((attribute) => (
      typeof attribute === 'string' ? attribute : `${attribute.field}^${(attribute.weight || 1) * boostMultiplier}`
    ));

    return {
      bool: {
        must: [{
          query_string: {
            query: sanitizedQuery,
            fields: getFieldsMap(2),
          },
        }],
        should: [
          {
            bool: {
              should: [
                {
                  multi_match: {
                    query: sanitizedQuery,
                    fields: getFieldsMap(1),
                    fuzziness: 'AUTO:4,8',
                  },
                },
                {
                  multi_match: {
                    query: sanitizedQuery,
                    fields: getFieldsMap(0.5),
                    type: 'bool_prefix',
                  },
                },
              ],
            },
          },
          {
            multi_match: {
              query: sanitizedQuery,
              type: 'phrase',
              fields: getFieldsMap(2),
            },
          },
        ],
      },
    };
  },
  hooks: {
    beforeSearch: (requests) => {
      const requestsWithOptions = requests;
      if (requestsWithOptions[0]) {
        // ElasticSearch track_total_hits will actually show the total hits,
        // instead of a maximum of 10.000.
        requestsWithOptions[0].body.track_total_hits = true;
      }

      // Per 10 years, we decrease the scoring by 25%
      // This way, older publications are less relevant
      requests.forEach((request) => {
        const { query } = request.body;
        delete request.body.query;

        request.body.query = {
          function_score: {
            query,
            functions: [
              {
                gauss: {
                  bibcite_year: {
                    origin: year,
                    scale: 10,
                    decay,
                  },
                },
              },
            ],
          },
        };
      });

      return new Promise((resolve) => {
        resolve(requests);
      });
    },
  },
  getBaseFilters: () => [
    {
      bool: {
        must: {
          term: {
            field_domain_access: {
              value: currentDomain,
            },
          },
        },
      },
    },
  ],
};
